import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Download } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import Tabs from '../../components/ui/Tabs';
import DataTable from '../../components/ui/DataTable';
import GenericSelect from '../../components/ui/GenericSelect';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import { readPage } from '../../utils/apiResult';
import { parseTableParams } from '../../utils/Utils';
import { useNineBoxProcesses } from '../nine-box/config';
import useTalentPoolsApi from '../talent-pool/config';
import { useTalentReportConfigs } from './config/index';

const Container = styled.div``;

const FilterRow = styled.div`
  max-width: 280px;
  margin-bottom: 16px;
`;

const ChartCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  margin-bottom: 16px;
`;

const ChartTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #004242;
  margin: 0 0 12px;
`;

const InfoBox = styled.div`
  background: #F5F7FA;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  padding: 40px 24px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

function ReportChart({ report, data }) {
  const { t } = useTranslation();
  const theme = useTheme();
  if (!report.hasChart || data.length === 0) return null;

  // Tema token'lı 5 slotluk kategorik palet + taşan dilimler için fold rengi —
  // DistributionChartCard deseni (eski PrdPerformanceReportsPage'den)
  // (hardcode renk YASAK); Cell key'i indeks değil satır kimliği (cellKey).
  const palette = [
    theme.colors.primary.main,
    theme.colors.secondary.dark,
    theme.colors.auxiliar.purple,
    theme.colors.auxiliar.danger,
    theme.colors.primary.dark,
  ];
  const foldColor = theme.colors.text.muted;

  const chartData = data.map((row) => ({
    cellKey: row.cellKey,
    name: t(`performance.ninebox.cell.${row.cellKey}`),
    count: row.count,
  }));

  return (
    <ChartCard>
      <ChartTitle>{report.label}</ChartTitle>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
          <Tooltip contentStyle={{ borderRadius: 6, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 13 }} />
          <Bar dataKey="count" name={t('performance.report.col.count')} radius={[4, 4, 0, 0]}>
            {chartData.map((entry, i) => (
              <Cell key={entry.cellKey} fill={i < palette.length ? palette[i] : foldColor} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function ReportTable({ report, extraParams }) {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 50, pageIndex: 0 });
  const [queryParams, setQueryParams] = useState({ pageSize: 50, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [exporting, setExporting] = useState(false);
  // Task 36 — flight-risk: yönetici için görünürlük kapalıysa BE 409 döner
  // (report.disabledKey işaretli, fetch skipErrorToast'lı). Genel "veri yok"
  // durumundan AYRI: bu, "bu rapor senin için kapalı" bilgi mesajıdır (bkz.
  // config/index.js yorumu).
  const [disabled, setDisabled] = useState(false);
  // TD-23 (kalan yüzeyler, Faz 2) — bkz. utils/apiResult.js. `disabled` ile
  // KARIŞTIRILMAMALI: o, "bu rapor senin için kapalı" ÜRÜN kuralıdır (409 +
  // disabledKey); bu ise okuma başarısızlığıdır. İkisini aynı boş-duruma
  // düşürmek, kapalı olmayan bir raporda sunucu hatasını "veri yok" gibi
  // gösteriyordu.
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchData = useCallback(async (params) => {
    setFetching(true);
    try {
      const res = await report.fetch({ ...params, ...extraParams });
      if (report.disabledKey && res.data?.status === false) {
        setDisabled(true);
        setData([]);
        setPagination({ totalRowCount: 0, pageIndex: 0, pageSize: params.pageSize || 50 });
        return;
      }
      setDisabled(false);
      const { ok, items, pagination: page } = readPage(res);
      setLoadFailed(!ok);
      setData(items);
      setPagination(page);
    } finally {
      setFetching(false);
    }
  }, [report, extraParams]);

  useEffect(() => { fetchData(queryParams); }, [queryParams, fetchData]);

  const handleDataStateChange = useCallback((_, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, report.columns);
    setQueryParams(params);
  }, [report]);

  const handleFilterConfirm = useCallback((fl) => setFilterList(fl), []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await report.export({ pageSize: 10000, pageNumber: 1, ...extraParams });
      // Başarısız GET'te interceptor headers'sız EMPTY_RESPONSE ({status:false})
      // resolve eder ve hatayı zaten toast'lamıştır — indirme yolunu sessizce
      // terk et (yoksa res.headers[...] TypeError → unhandled rejection).
      if (!res?.headers || res.data?.status === false) return;
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const match = res.headers['content-disposition']?.match(/filename[^;=\n]*=([^;\n]*)/);
      a.download = match ? match[1].replaceAll('"', '') : `${report.slug}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [report, extraParams]);

  if (disabled) {
    return <InfoBox>{t(`responseErrors.${report.disabledKey}`)}</InfoBox>;
  }

  return (
    <div>
      {/* TD-23 — başarısız okuma bandı. Grafik ve dışa aktarma da bu veriyi
          kullanır: eksik seriyi "veri yok" diye sunmak yanlış karar ürettirir. */}
      {loadFailed && <LoadErrorBanner />}
      <ReportChart report={report} data={data} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button type="button" $variant="outline" onClick={handleExport} disabled={exporting || data.length === 0}>
          <Download size={14} style={{ marginRight: 6 }} />
          {exporting ? t('common.loading') : t('performance.report.export')}
        </Button>
      </div>
      <DataTable
        title={report.label}
        fetching={fetching}
        columns={report.columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
        disableFilters
      />
    </div>
  );
}

export default function TalentReportsPage() {
  const { t } = useTranslation();
  const location = useLocation();

  const reports = useTalentReportConfigs();
  const { processes } = useNineBoxProcesses();
  const { data: pools } = useTalentPoolsApi();

  const [processId, setProcessId] = useState(null);
  const [poolId, setPoolId] = useState(null);

  const initialTab = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const tab = qs.get('tab');
    return reports.find((r) => r.slug === tab)?.slug || reports[0].slug;
  }, [location.search, reports]);

  const [activeTab, setActiveTab] = useState(initialTab);

  const tabs = reports.map((r) => ({ key: r.slug, label: r.label }));
  const active = reports.find((r) => r.slug === activeTab) || reports[0];

  const processOptions = processes.map((p) => ({ id: p.id, name: p.name }));
  const poolOptions = (pools || []).map((p) => ({ id: p.id, name: p.name }));

  const extraParams = useMemo(() => {
    const params = {};
    if (active.needsProcess && processId) params['processId[eq]'] = processId;
    if (active.needsPool && poolId) params['poolId[eq]'] = poolId;
    return params;
  }, [active, processId, poolId]);

  return (
    <Container>
      <Breadcrumbs items={[{ label: t('menu.talentReports') }]} />
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {active.needsProcess && (
        <FilterRow>
          <GenericSelect
            label={t('performance.report.processFilter')}
            value={processId}
            onChange={setProcessId}
            options={processOptions}
            idKey="id"
            labelKey="name"
            placeholder={t('performance.report.autoLatestProcess')}
          />
        </FilterRow>
      )}

      {active.needsPool && (
        <FilterRow>
          <GenericSelect
            label={t('performance.report.poolFilter')}
            value={poolId}
            onChange={setPoolId}
            options={poolOptions}
            idKey="id"
            labelKey="name"
            placeholder={t('performance.report.allPools')}
          />
        </FilterRow>
      )}

      <div style={{ marginTop: 16 }}>
        <ReportTable key={active.slug} report={active} extraParams={extraParams} />
      </div>
    </Container>
  );
}
